/**
 * Shared building blocks for premium centred-dialog popups (detail sheets +
 * form sheets). Keeping them in one place so every dialog reads as the same
 * product — same tab style, same section headings, same hero accents.
 */

import * as React from 'react';

/**
 * Underlined-link tab style — overrides the default shadcn pill so tabs
 * blend into the dialog surface (Stripe / Linear pattern). Apply to every
 * `<TabsTrigger>` inside a centred dialog so the strip reads as one row of
 * underlined links rather than a pill cluster.
 *
 * Pair with `<TabsList className="h-10 gap-2 bg-transparent p-0" />` and
 * wrap the list in `<div className="border-b border-border/60 px-7" />`.
 */
export const DIALOG_TAB_TRIGGER =
  'inline-flex h-10 items-center gap-1.5 rounded-none border-b-2 border-transparent bg-transparent px-3 text-[12.5px] font-medium text-muted-foreground transition-colors hover:text-foreground data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none';

interface SectionHeadingProps {
  title: React.ReactNode;
  hint?: React.ReactNode;
  /** Optional right-aligned slot — counts, action buttons, etc. */
  action?: React.ReactNode;
}

/**
 * Small leading section header used inside dialog tabs. Title left, hint
 * (or action) right, both on a single baseline. No bottom border — let the
 * surrounding section provide its own visual grouping.
 */
export function SectionHeading({ title, hint, action }: SectionHeadingProps) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <h3 className="text-[12.5px] font-semibold tracking-tight text-foreground">
        {title}
      </h3>
      {action ? (
        <div>{action}</div>
      ) : hint ? (
        <span className="text-[11px] text-muted-foreground">{hint}</span>
      ) : null}
    </div>
  );
}

/**
 * Tiny eyebrow text used above a dialog title — typically for context like
 * "Edit · BX-CORR-L" or a category breadcrumb. Lives in its own component
 * so the type ramp is consistent across all dialogs.
 */
export function DialogEyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10.5px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
      {children}
    </div>
  );
}
